import React from 'react'
import { applyMiddleware, combineReducers, createStore, compose } from 'redux'
import createSagaMiddleware from 'redux-saga'
import i18next from 'i18next'
import bowser from 'bowser'
import { Provider } from 'react-redux'
import { baseReducer, sessionReducer } from './redux/reducers'
import { login, funMoneyLogin, setOrientationChecked, error,
  goToLobby, loadAssets, setCheats, setDemo, setHasSplash,
  onSplashStart, setHasIntro, hideIntro } from './redux/actions'
import baseSagas from './redux/sagas'
import { HidePrePreLoader } from './redux/sagas/loading'
import Layout from './components/mainlayout'
import { READY, LOCALE_LOADED, } from './redux/constants'
import { getFromLocalStorage } from './Utils'
import Currency from './Utils/Currency'
import compatibilityCheck from './Utils/compatibilityCheck'
import configBridge from './Utils/configBridge'
import LoadTranslation from './Utils/translationLoader'
import config from "config"
import initialize from './initializations'

require('./css/default.less')
require('./css/font-awesome.min.css')
require('@rlx/feim')

/**
 * Main component
 *
 * This is the heart of MacGuffin.
 * This component creates the react and redux contexts and also
 * hosts and runs everything game-related.
 *
 * This component accepts the following props:
 * * Game (the actual main component of the game)
 * * Assets (a list of all assets to be loaded)
 * * Sagas (all sagas that is connected to the game, will be initiated on the "game" store)
 * * Reducer (the reducert that is connected to the game, will modify "game" store)
 * * LoadingScreen (the component that will host the loading screen)
 * * SplashScreen (OPTIONAL, the component that will host the splash screen)
 * * IntroMovie (OPTIONAL, location of asset to be used for the intro movie)
 * * Components (a list of "extra" components that will be rendered)
 * * Cheats (OPTIONAL, object where the "keys" is the name of the cheat, and the values JSON encoded strings with game-specific cheat)
 * * Demo (OPTIONAL, array where the entries are JSON encoded strings with a game-specific cheat)
 * * ReduxDevToolsOptions (OPTIONAL, object that specifies extension's options)
 *
 * @example
 * import ReactDOM from 'react-dom'
 * import { Main } from 'macguffin'
 * import Renderer from 'mc_pixi'
 * import UI from 'mc_full-slot_ui'
 * import Game, { Assets, Sagas, Reducer, SettingsPages, LoadingScreen, SplashScreen } from './game'
 *
 * const  IntroMovie = 'assets/intro-movie.mp4'
 *
 * UI.props = { SettingsPages }
 *
 * const Components = { UI, Renderer }
 *
 * const Cheats = {
 *   'Trigger Freespins': '{"feature":true}'
 * }
 *
 * const Demo = [
 *  '{"feature":false}',
 *  '{"feature":true}'
 * ]
 *
 * ReactDOM.render(<Main {...{ Game, Assets, Sagas, Reducer, LoadingScreen, IntroMovie, SplashScreen, Components, Cheats, Demo }} />, document.getElementById('pixi-box'))
 */
export default class Main extends React.Component {
  state = { ready: false }
  /**
   * Handles launching of the game.
   */
  async componentWillMount() {
    await configBridge()

    if (config.analyticsURL && !config.disableAnalytics) {
      const script = document.createElement('script')
      script.src = config.analyticsURL
      document.head.appendChild(script)
    }

    const Components = this.getComponents()

    const reducers = { base: baseReducer, session: sessionReducer }

    if (this.props.Reducer)
      reducers.game = this.props.Reducer

    const context = require.context('./components/', true, /^(.*\/reducer\.(js$))[^.]*$/im)

    context.keys().forEach(key => {
      let component = context(key)
      const { name } = component

      if (component.default)
        component = component.default

      reducers[name] = component
    })

    Object.keys(Components).forEach(key => {
      const { Reducer } = Components[key]

      if (!Reducer)
        return

      const { name } = Reducer

      reducers[name || key] = Reducer.default || Reducer
    })

    this.reducer = combineReducers(reducers)
    
    const sagaMiddleware = createSagaMiddleware(
      process.env.NODE_ENV !== 'production' && this.props.SagaMiddlewareOptions ?
      this.props.SagaMiddlewareOptions :
      {}
    )

    let composeEnhancers
    if (typeof window === 'object'
      && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      && process.env.NODE_ENV !== 'production'
    ) {
      composeEnhancers = this.props.ReduxDevToolsOptions ?
        window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__(this.props.ReduxDevToolsOptions) :
        window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({})
    } else {
      composeEnhancers = compose
    }

    this.store = createStore(this.reducer, composeEnhancers(applyMiddleware(sagaMiddleware)))
    Currency.setStore(this.store)

    this.setState({ ready: true })

    sagaMiddleware.run(baseSagas)

    if (this.props.Sagas) {
      sagaMiddleware.run(this.props.Sagas)
    }

    Object.values(Components).forEach(Component => {
      const { Sagas } = Component

      if (Sagas) {
        sagaMiddleware.run(Sagas)
      }
    })

    if (window.__VERSIONBLOCKED__) {
      alert("Game out of date")
      this.store.dispatch(goToLobby())
      return
    }

    this.readyListener()

    const { base, session } = this.store.getState()

    LoadTranslation(this.store, session.locale, onLocaleLoaded.bind(this))

    // Integrate dynamic integrations
    const dynamicIntegrations = require.context('./integrations/', true, /^(.*\/index\.(js$))[^.]*$/im)
    dynamicIntegrations.keys().forEach(key => {
      const component = dynamicIntegrations(key)
      component && (component.default || component).init(this.store)
    })

    const dynamicIntegrationSagas = require.context('./integrations/', true, /^(.*\/sagas\.(js$))[^.]*$/im)
    dynamicIntegrationSagas.keys().forEach(key => {
      const component = dynamicIntegrationSagas(key)
      sagaMiddleware.run(component.default || component)
    })

    const usesMovie =  this.props.IntroMovie && !bowser.mobile && !base.hideIntro &&
      !bowser.msie && !document.location.href.includes('pokerclient')
    const skipIntro = getFromLocalStorage('skipIntro') === 'true'

    if (usesMovie) {
      this.store.dispatch(setHasIntro(true))

      if (skipIntro) {
        this.store.dispatch(hideIntro())
      } else {
        if (window.usesNewPreloader) {
          HidePrePreLoader()
        }

        await this.listenToState(cb => {
          if (this.store.getState().base.introLoaded) {
            cb()
          }
        })
      }
    }

    const splashEnabled = this.props.SplashScreen && !base.hideSplash

    if (splashEnabled) {
      this.store.dispatch(setHasSplash(true))
    }

    if (this.props.Assets)
      this.store.dispatch(loadAssets(this.props.Assets))

    if (usesMovie && !skipIntro) {
      await this.listenToState(cb => {
        if (this.store.getState().base.hideIntro) {
          cb()
        }
      })
    }

    if (splashEnabled) {
      this.store.dispatch(onSplashStart())
    }

    const dynamicSagas = require.context('./components/', true, /^(.*\/sagas\.(js$))[^.]*$/im)

    dynamicSagas.keys().forEach(key => {
      const component = dynamicSagas(key)
      sagaMiddleware.run(component.default || component)
    })

    if (this.props.Cheats) {
      this.store.dispatch(setCheats(this.props.Cheats))
    }

    if (this.props.Demo) {
      this.store.dispatch(setDemo(this.props.Demo))
    }

    Object.values(Components).forEach(component => {
      const { Saga } = component

      if (!Saga)
        return

      sagaMiddleware.run(Saga.default || Saga)
    })

    preventZoomOnIOS()

    const ignoreFullscreen = session.fullscreen === 'false'

    if (!ignoreFullscreen) {
      if (bowser.mobile && !this.enableSwipeCheck())
        this.enableFullscreenRequests()
    }

    initialize.call(this, this.store)
  }

  /**
   * Will dispatch loadAssets, if there are new assets.
   * @param nextProps
   */
  componentWillReceiveProps(nextProps) {
    if (this.store && nextProps.Assets !== this.props.Assets)
      this.store.dispatch(loadAssets(nextProps.Assets))
  }

  listenToState = listener => {
    let unsubscribe

    return new Promise(resolve => {
      unsubscribe = this.store.subscribe(() => listener(resolve))
    }).then(unsubscribe)
  }

  getComponents = () => {
    const { Renderer, Components } = this.props
    const ret = { ...Components }
    if (Renderer) {
      ret.Renderer = Renderer
    }
    return ret
  }

  /**
   * This method will attempt to scroll to the top of the page and also try to see if the page is fullscreen.
   */
  onResize = () => {
    if (window.resizeTimeout) {
      clearTimeout(window.resizeTimeout)
    }

    window.resizeTimeout = setTimeout(() => {
      window.scrollTo(0, 0)
    }, 1000)

    const landscape = window.innerWidth > window.innerHeight
    const navigationHeight = window.screen.height - window.innerHeight
    const landscapeCheck = window.screen.width - window.innerHeight
    const { store } = this
    store.dispatch(setOrientationChecked(landscape ? landscapeCheck < 50 : navigationHeight < 80))
  }

  /**
   * Sets the orientationChecked variable to false when the device changes orientation.
   * This is so that the client will attempt to go fullscreen again after orientation changed.
   */
  enableFullscreenRequests = () => {
    const { store } = this
    store.dispatch(setOrientationChecked(false))

    window.addEventListener('resize', () => {
      store.dispatch(setOrientationChecked(false))
    })
  }

  /**
   * Adds a check for iOS devices, that will force the user to swipe for fullscreen.
   */
  enableSwipeCheck = () => {
    const isReallySafari = navigator && /safari/i.test(navigator.userAgent)

    if (!bowser.ios || !bowser.safari || !isReallySafari) {
      return false
    }
    setOrientationChecked(false)
    window.addEventListener('resize', this.onResize)
    document.onfocusin = document.onfocusout = this.onResize
    window.onpageshow = window.onpagehide = window.onfocus = window.onblur = this.onResize

    if (bowser.safari && bowser.ios) {
      document.documentElement.style.paddingBottom = '100vh'
    }

    return true
  }

  readyListener = async () => {
    const { store } = this

    await this.listenToState(cb => {
      const { base } = this.store.getState()
      if (!base.loading && base.loggedIn &&
        (!base.hasIntro || base.hideIntro)) {
        cb()
      }
    })

    store.dispatch({ type: READY })
  }

  /**
   * The main render method, which will render the components passed down to it from the game project.
   * @returns {*}
   */
  render() {
    const { ready } = this.state
    const { Game, LoadingScreen, SplashScreen, IntroMovie, ConfigurableVariables } = this.props
    const Components = this.getComponents()

    if (!ready || !Game)
      return <div />

    return (
      <Provider store={this.store} key="provider">
        <Layout
          {...{
            Game,
            Components,
            LoadingScreen,
            SplashScreen,
            IntroMovie,
            ConfigurableVariables
          }}
        />
      </Provider>
    )
  }
}

/**
 * Function that will attempt to login after the locales have been loaded.
 * If no ticket exists, the client will go back to the Lobby / previous page.
 */
function onLocaleLoaded(locale) {
  const { session } = this.store.getState()

  this.store.dispatch({ type: LOCALE_LOADED, locale })

  compatibilityCheck(this.store)

  if (session.moneymode === 'fun') {
    this.store.dispatch(funMoneyLogin())
  } else if (session.ticket) {
    this.store.dispatch(login())
  } else {
    this.store.dispatch(error({
      errorCode: 1,
      errorMessage: i18next.t('error.noLoginTicket'),
      onClose: () => {
        if (!this.isReturning) { // Prevent multiple clicks from firing
          this.isReturning = true
          this.store.dispatch(goToLobby())
        }
      }
    }))
  }
}

/**
 * Function to attempt double tapping and pinching (for zooming) on safari browser on ios devices.
 */
const preventZoomOnIOS = () => {
  if (bowser.ios && bowser.safari) {
    document.addEventListener('gesturestart', e => {
      e.preventDefault()
      e.stopPropagation()
    }, false)
    document.addEventListener('touchstart', e => {
      if (e.touches.length > 1) {
        e.preventDefault()
        e.stopPropagation()
      }
    }, false)
    document.addEventListener('touchmove', e => {
      if (e.scale !== 1) {
        e.preventDefault()
        e.stopPropagation()
      }
    }, false)
    let lastTouchEnd = 0
    document.addEventListener('touchend', e => {
      const now = (new Date()).getTime()
      if (now - lastTouchEnd <= 450) {
        e.preventDefault()
        e.stopPropagation()
        const svgAncestor = e.target.closest('.svg-icon')
        if (e.target.click) {
          e.target.click()
        } else if (svgAncestor) {
          svgAncestor.dispatchEvent(new CustomEvent('onClickForIosDelay', e))
        }
      }
      lastTouchEnd = now
    }, false)
  }
}
